import type { FinancingApplication } from './financing.application.model';
import type { FinancingOffer } from './financing.offer.model';

/**
 * Number of days after the repayment due date before a `funded`
 * application is treated as **late** in the UI. Mirrors
 * `FINANCING_CONFIG.repayment.lateAfterDays` on the backend — keep
 * them in sync until the sweep cron lands and owns the `late` tag
 * authoritatively.
 *
 * Lives in `@hero/schema` so both dashboards (carrier + admin) can
 * read it without pulling in a backend-only config module.
 */
export const FINANCING_LATE_AFTER_DAYS = 7;

/**
 * Return the offer the carrier accepted (`decision: 'accepted'`),
 * or `undefined` when none. Replaces the legacy
 * `application.acceptedOfferId` pointer — the offer's own decision
 * is now the single source of truth.
 */
export function getAcceptedOffer(
  application: Pick<FinancingApplication, 'offers'>,
): FinancingOffer | undefined {
  return application.offers?.find(o => o.decision === 'accepted');
}

/**
 * Convenience accessor for the accepted offer's id. Returns
 * `undefined` when no offer has been accepted yet.
 */
export function getAcceptedOfferId(
  application: Pick<FinancingApplication, 'offers'>,
): string | undefined {
  return getAcceptedOffer(application)?.id;
}

/**
 * `true` iff this offer is the one the carrier accepted. Cheap
 * predicate for UI badges / per-row gating.
 */
export function isAcceptedOffer(offer: Pick<FinancingOffer, 'decision'>): boolean {
  return offer.decision === 'accepted';
}

/**
 * `true` iff the lender submitted concrete terms on this row —
 * either still open for acceptance (`offered`) or already locked in
 * (`accepted`). Use for "show terms grid" / "show repayment date"
 * gates that don't care about which side of acceptance we're on.
 */
export function offerHasTerms(offer: Pick<FinancingOffer, 'decision'>): boolean {
  return offer.decision === 'offered' || offer.decision === 'accepted';
}

/**
 * `true` iff the lender has progressed past `'pending'` — they
 * either clicked "I'm interested", submitted concrete terms, were
 * accepted by the carrier, or explicitly declined. Used as the
 * single live "contact-revealed" signal across the partner /
 * carrier UIs so contact-masking and the "Contact lender" affordance
 * flip the moment the lender engages. The `offer.interestedAt`
 * stamp remains the historic audit trail.
 */
export function isOfferEngaged(
  offer: Pick<FinancingOffer, 'decision'>,
): boolean {
  return offer.decision !== 'pending';
}

/** Day in milliseconds — local to keep this file dependency-free. */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolve the repayment due date for an application. Order of
 * preference:
 *  1. The accepted offer's explicit `terms.repaymentDate` (lender
 *     wrote the date directly).
 *  2. The accepted offer's `terms.term.dueDate` (inherited from the
 *     shared `termDurationSchema`).
 *  3. `outcome.fundedAt` + the accepted offer's term value
 *     (interpreted via `term.type`), falling back to
 *     `requestedTerm` when no offer was accepted yet.
 *  4. `undefined` when neither signal is available (e.g. not yet
 *     funded).
 *
 * Returned as a `Date` in UTC; callers format for display.
 */
export function getFinancingRepaymentDueDate(
  application: Pick<
    FinancingApplication,
    'offers' | 'outcome' | 'requestedTerm'
  >,
): Date | undefined {
  const accepted = getAcceptedOffer(application);
  const explicitDueDate =
    accepted?.terms?.repaymentDate ?? accepted?.terms?.term?.dueDate;
  if (explicitDueDate) return new Date(explicitDueDate);

  const fundedAt = application.outcome?.fundedAt;
  if (!fundedAt) return undefined;

  const baseTerm = accepted?.terms?.term ?? application.requestedTerm;
  const fundedMs = new Date(fundedAt).getTime();
  const days = (() => {
    switch (baseTerm.type) {
      case 'day':
        return baseTerm.value;
      case 'week':
        return baseTerm.value * 7;
      case 'month':
        return baseTerm.value * 30;
      case 'immediate':
        return 0;
      case 'manual':
        return baseTerm.value;
      default:
        return baseTerm.value;
    }
  })();
  return new Date(fundedMs + days * DAY_MS);
}

/**
 * Derived "is this row late?" — true when the application is funded
 * (or awaiting repayment approval) AND the due date is more than
 * `FINANCING_LATE_AFTER_DAYS` in the past. Admin-applied `tags[]`
 * also count so the UI agrees with manual overrides.
 *
 * `repaid` and `defaulted` are terminal and return `false` so badges
 * don't linger after the loan closes.
 */
export function isFinancingApplicationLate(
  application: Pick<
    FinancingApplication,
    'status' | 'tags' | 'offers' | 'outcome' | 'requestedTerm'
  >,
  now: Date = new Date(),
): boolean {
  if (application.status === 'repaid' || application.status === 'defaulted') {
    return false;
  }
  const tags = application.tags ?? [];
  if (tags.includes('late') || tags.includes('overdue')) return true;

  if (
    application.status !== 'funded' &&
    application.status !== 'awaiting_repayment_approval'
  ) {
    return false;
  }
  const dueDate = getFinancingRepaymentDueDate(application);
  if (!dueDate) return false;
  const diffDays = (now.getTime() - dueDate.getTime()) / DAY_MS;
  return diffDays > FINANCING_LATE_AFTER_DAYS;
}
